import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { useAuth } from '../context/AuthContext';

const OTP_LENGTH = 6;

const Login = () => {
    const navigate = useNavigate();
    const { login: authLogin, verifyOtp: authVerifyOtp, isAuth, user } = useAuth();

    // Step state: 1 = Credentials, 2 = OTP Verification
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });

    const [sessionToken, setSessionToken] = useState('');
    const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
    const [infoMessage, setInfoMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Resend Timer & state
    const [resendTimer, setResendTimer] = useState(60);
    const [isResending, setIsResending] = useState(false);

    const inputRefs = useRef([]);

    useEffect(() => {
        if (isAuth && user) {
            if (user?.role === 'KTE-management' || user?.accountType === 'KTE-management') {
                navigate('/management/pending-orders', { replace: true });
            } else if (user?.role === 'Supplier' || user?.accountType === 'Supplier') {
                navigate('/supplier/pending-orders', { replace: true });
            } else {
                navigate('/order-request', { replace: true });
            }
        }
    }, [isAuth, user, navigate]);

    // Resend countdown timer effect
    useEffect(() => {
        let interval = null;
        if (step === 2 && resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [step, resendTimer]);

    // Focus first OTP cell when entering Step 2
    useEffect(() => {
        if (step === 2) {
            setResendTimer(60);
            setOtpDigits(Array(OTP_LENGTH).fill(''));
            setTimeout(() => {
                inputRefs.current[0]?.focus();
            }, 100);
        }
    }, [step]);

    const handleCredentialsChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name] || errors.general) {
            setErrors(prev => ({
                ...prev,
                [name]: '',
                general: ''
            }));
        }
    };

    const handleOtpDigitChange = (index, value) => {
        const sanitized = value.replace(/\D/g, '');
        if (!sanitized) {
            const updated = [...otpDigits];
            updated[index] = '';
            setOtpDigits(updated);
            return;
        }

        const digit = sanitized.slice(-1);
        const updated = [...otpDigits];
        updated[index] = digit;
        setOtpDigits(updated);

        if (errors.otpCode || errors.general) {
            setErrors(prev => ({ ...prev, otpCode: '', general: '' }));
        }

        if (index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!otpDigits[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').trim();
        const digits = pasteData.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
        if (digits.length > 0) {
            const updated = Array(OTP_LENGTH).fill('');
            digits.forEach((d, i) => {
                updated[i] = d;
            });
            setOtpDigits(updated);
            if (errors.otpCode || errors.general) {
                setErrors(prev => ({ ...prev, otpCode: '', general: '' }));
            }
            const focusIdx = Math.min(digits.length, OTP_LENGTH - 1);
            inputRefs.current[focusIdx]?.focus();
        }
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }
        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const otpCode = otpDigits.join('');
        const newErrors = {};
        if (otpCode.length < OTP_LENGTH) {
            newErrors.otpCode = `Please enter all ${OTP_LENGTH} digits of the OTP code`;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStep1Submit = async (e) => {
        e.preventDefault();

        if (!validateStep1()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            const res = await authLogin(formData);
            if (res && res.sessionToken) {
                setSessionToken(res.sessionToken);
                setInfoMessage(res.message || 'OTP sent to your registered email address');
                setStep(2);
            } else {
                throw new Error('Invalid response from server - session token missing');
            }
        } catch (error) {
            console.error('Login step 1 error:', error);
            let message = error.message || 'Login failed. Please try again.';
            if (message.includes('Network error')) {
                message = 'Cannot connect to the server. Please check your internet connection or try again later.';
            } else if (message.toLowerCase().includes('invalid credentials') || message.toLowerCase().includes('incorrect')) {
                message = 'Incorrect username or password.';
            }
            setErrors({ general: message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleStep2Submit = async (e) => {
        e.preventDefault();

        if (!validateStep2()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            const otpCode = otpDigits.join('');
            await authVerifyOtp({ sessionToken, otpCode });
        } catch (error) {
            console.error('OTP verification error:', error);
            let message = error.message || 'OTP verification failed. Please try again.';
            if (message.includes('Network error')) {
                message = 'Cannot connect to the server. Please check your internet connection or try again later.';
            } else if (message.toLowerCase().includes('session token') || message.toLowerCase().includes('expired session')) {
                message = 'Session expired. Please log in again with your credentials.';
            } else if (message.toLowerCase().includes('invalid') || message.toLowerCase().includes('used') || message.toLowerCase().includes('otp')) {
                message = 'Invalid, expired, or already used OTP code.';
            }
            setErrors({ general: message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (resendTimer > 0 || isResending || isLoading) return;

        setIsResending(true);
        setErrors({});

        try {
            const res = await authLogin(formData);
            if (res && res.sessionToken) {
                setSessionToken(res.sessionToken);
                setInfoMessage(res.message || 'A new OTP code has been sent to your registered email address');
                setResendTimer(60);
                setOtpDigits(Array(OTP_LENGTH).fill(''));
                inputRefs.current[0]?.focus();
            } else {
                throw new Error('Invalid response from server - session token missing');
            }
        } catch (error) {
            console.error('Resend OTP error:', error);
            let message = error.message || 'Failed to resend OTP code. Please try again.';
            if (message.includes('Network error')) {
                message = 'Cannot connect to the server. Please check your internet connection or try again later.';
            }
            setErrors({ general: message });
        } finally {
            setIsResending(false);
        }
    };

    const handleBackToStep1 = () => {
        setStep(1);
        setSessionToken('');
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setInfoMessage('');
        setErrors({});
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden">
            <div
                className="absolute inset-0 w-full h-full bg-cover bg-center z-0"
                style={{ backgroundImage: 'url(/bg.webp)' }}
                aria-hidden="true"
            />
            <div className="fixed top-20 right-40 z-30">
                <img src="/KTE-logo.webp" alt="KT Logo" className="w-[240px] object-contain" />
            </div>
            <div
                className="relative z-20 w-full max-w-md sm:max-w-md md:max-w-lg lg:max-w-md bg-white bg-opacity-90 rounded-lg shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm mx-8 sm:mx-4 mt-16"
                style={{
                    boxShadow: `0 0px 20px 0 rgba(30,41,59,0.25)`
                }}
            >
                {/* Back to Sign In button in top-left corner */}
                {step === 2 && (
                    <button
                        type="button"
                        onClick={handleBackToStep1}
                        className="absolute top-4 left-4 sm:top-6 sm:left-6 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Back to Sign In"
                        aria-label="Back to Sign In"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                )}

                <div className="text-center mb-6 sm:mb-8">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {step === 1 ? 'Welcome Back' : 'Enter Verification Code'}
                    </h2>
                    <p className="text-gray-600 mt-1 text-sm">
                        {step === 1
                            ? 'Sign in to access the Dealers Portal'
                            : 'Step 2: Enter the 6-digit OTP sent to your email'}
                    </p>
                </div>

                {errors.general && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4 text-sm">
                        {errors.general}
                    </div>
                )}

                {step === 1 ? (
                    <form className="space-y-4 sm:space-y-6" onSubmit={handleStep1Submit}>
                        <Input
                            label="Username"
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleCredentialsChange}
                            error={errors.username}
                            required
                            placeholder="e.g., dealer"
                        />

                        <Input
                            label="Password"
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleCredentialsChange}
                            error={errors.password}
                            required
                            placeholder="Enter your password"
                        />

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={isLoading}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300"
                        >
                            {isLoading ? 'Signing In...' : 'Sign In'}
                        </Button>
                    </form>
                ) : (
                    <form className="space-y-4 sm:space-y-6" onSubmit={handleStep2Submit}>
                        {/* 6-cell OTP digit inputs */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                                Verification Code <span className="text-red-500">*</span>
                            </label>
                            <div className="flex justify-center items-center gap-2 sm:gap-3" onPaste={handleOtpPaste}>
                                {otpDigits.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={(el) => (inputRefs.current[idx] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                        className={`w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-bold rounded-lg border focus:outline-none focus:ring-2 transition-all duration-200 ${
                                            errors.otpCode || errors.general
                                                ? 'border-red-400 focus:ring-red-500 bg-red-50 text-red-900'
                                                : digit
                                                ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/40 text-gray-800'
                                                : 'border-gray-300 focus:ring-blue-500 focus:border-transparent bg-white text-gray-800'
                                        }`}
                                    />
                                ))}
                            </div>
                            {errors.otpCode && (
                                <p className="mt-2 text-sm text-red-600 text-center">{errors.otpCode}</p>
                            )}
                        </div>

                        {/* Resend OTP button & timer */}
                        <div className="text-center pt-1">
                            {resendTimer > 0 ? (
                                <p className="text-xs sm:text-sm text-gray-500">
                                    Resend code in <span className="font-semibold text-blue-600">{resendTimer}s</span>
                                </p>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleResendCode}
                                    disabled={isResending || isLoading}
                                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline transition-colors focus:outline-none disabled:opacity-50"
                                >
                                    {isResending ? 'Resending Code...' : 'Resend Code'}
                                </button>
                            )}
                        </div>

                        <div className="pt-2">
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={isLoading || isResending}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300"
                            >
                                {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
